/**
 * Shared PDF layout components for @react-pdf/renderer
 * Used by all 18 exam generators.
 */

import React from 'react';
import { View, Text, Page } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, getFacilityDisplayName } from './pdfHelpers';

// ---------------------------------------------------------------------------
// Page Header: top border + facility name + blue title bar
// ---------------------------------------------------------------------------
export function PdfPageHeader({ facilityInfo, examTitle }) {
  const facilityName = getFacilityDisplayName(facilityInfo);
  return (
    <View>
      <View style={commonStyles.headerTopBorder} />
      <Text style={commonStyles.facilityName}>{facilityName}</Text>
      <View style={commonStyles.titleBar}>
        <Text style={commonStyles.titleBarText}>{examTitle}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Patient Information Box
// ---------------------------------------------------------------------------
export function PdfPatientBox({ firstName, lastName, mrn, examDate, technologist, reason, technologistLabel = 'Technologist', studentWorksheet, supervisingSonographer }) {
  return (
    <View style={commonStyles.patientBox}>
      <Text style={commonStyles.patientBoxTitle}>PATIENT INFORMATION</Text>
      <View style={commonStyles.patientRow}>
        <View style={commonStyles.patientCell}>
          <Text style={commonStyles.patientLabel}>Patient Name:</Text>
          <Text style={commonStyles.patientValue}>{firstName} {lastName}</Text>
        </View>
        <View style={commonStyles.patientCell}>
          <Text style={commonStyles.patientLabel}>MRN:</Text>
          <Text style={commonStyles.patientValue}>{mrn}</Text>
        </View>
      </View>
      <View style={commonStyles.patientRow}>
        <View style={commonStyles.patientCell}>
          <Text style={commonStyles.patientLabel}>Exam Date:</Text>
          <Text style={commonStyles.patientValue}>{formatExamDate(examDate)}</Text>
        </View>
        <View style={commonStyles.patientCell}>
          <Text style={commonStyles.patientLabel}>{technologistLabel}:</Text>
          <Text style={commonStyles.patientValue}>
            {technologist || ''}{studentWorksheet && technologist ? ' (Student)' : ''}
          </Text>
        </View>
      </View>
      {studentWorksheet ? (
        <View style={commonStyles.patientRow}>
          <View style={commonStyles.patientCell} />
          <View style={commonStyles.patientCell}>
            <Text style={commonStyles.patientLabel}>Supervising Sonographer:</Text>
            <Text style={commonStyles.patientValue}>{supervisingSonographer || ''}</Text>
          </View>
        </View>
      ) : null}
      {reason ? (
        <View style={commonStyles.patientRow}>
          <Text style={commonStyles.patientLabel}>Reason:</Text>
          <Text style={[commonStyles.patientValue, { flex: 1 }]}>{reason}</Text>
        </View>
      ) : null}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Call Report Banner
// ---------------------------------------------------------------------------
export function PdfCallReport({ show, phoneNumber }) {
  if (!show || !phoneNumber) return null;
  return (
    <View style={commonStyles.callReportBox}>
      <Text style={commonStyles.callReportText}>CALL REPORT: {phoneNumber}</Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Section Header (dark, used in full-width single-column sections)
// ---------------------------------------------------------------------------
export function PdfSectionHeader({ title }) {
  return <Text style={commonStyles.sectionHeader}>{title}</Text>;
}

// ---------------------------------------------------------------------------
// Accent Section Header (blue, used in two-column layouts)
// ---------------------------------------------------------------------------
export function PdfAccentSectionHeader({ title }) {
  return <Text style={commonStyles.accentSectionHeader}>{title}</Text>;
}

// ---------------------------------------------------------------------------
// Label + Value row with normal/abnormal colouring
// ---------------------------------------------------------------------------
export function PdfLabelValue({ label, value, isAbnormal, forceBlack }) {
  const valueStyle = forceBlack
    ? commonStyles.valueDefault
    : isAbnormal
      ? commonStyles.valueAbnormal
      : commonStyles.valueNormal;

  return (
    <View style={commonStyles.row}>
      <Text style={commonStyles.label}>{label}</Text>
      <Text style={valueStyle}>{value}</Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Measurement box (blue border / light blue background)
// ---------------------------------------------------------------------------
export function PdfMeasurementBox({ title, children }) {
  return (
    <View>
      {title ? <Text style={commonStyles.accentSectionHeader}>{title}</Text> : null}
      <View style={commonStyles.measurementBox}>
        {children}
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Comments box (amber border / light yellow background)
// ---------------------------------------------------------------------------
export function PdfCommentsBox({ comments }) {
  if (!comments) return null;
  return (
    <View>
      <Text style={commonStyles.accentSectionHeader}>COMMENTS</Text>
      <View style={commonStyles.commentsBox}>
        <Text style={commonStyles.infoBoxText}>{comments}</Text>
      </View>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Page footer with page number
// ---------------------------------------------------------------------------
export function PdfFooter({ facilityInfo }) {
  const facilityName = getFacilityDisplayName(facilityInfo);
  return (
    <View style={commonStyles.footer} fixed>
      <Text style={commonStyles.footerText}>{facilityName} — Ultrasound Worksheet</Text>
      <Text
        style={commonStyles.footerText}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  );
}

// ---------------------------------------------------------------------------
// Convenience: full standard page wrapper
// ---------------------------------------------------------------------------
export function PdfStandardPage({ facilityInfo, examTitle, data, children }) {
  const reason = data.reason
    ? [data.reason, data.history ? String(data.history) : ''].filter(Boolean).join('. ')
    : '';

  return (
    <Page size="LETTER" style={commonStyles.page}>
      <PdfPageHeader facilityInfo={facilityInfo} examTitle={examTitle} />
      <PdfPatientBox
        firstName={data.firstName}
        lastName={data.lastName}
        mrn={data.mrn}
        examDate={data.examDate}
        technologist={data.technologist}
        studentWorksheet={data.studentWorksheet}
        supervisingSonographer={data.supervisingSonographer}
        reason={reason}
      />
      <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />
      {children}
      <PdfFooter facilityInfo={facilityInfo} />
    </Page>
  );
}
